/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

/**
 * Formats a date to display time in a readable format
 */
export function formatTime(date: Date): string {
  try {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.warn('Failed to format time with toLocaleTimeString, using fallback:', error);
    // Fallback to a simple time format
    const hours = date?.getHours?.() || 0;
    const minutes = date?.getMinutes?.() || 0;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes?.toString?.().padStart(2, '0') || '00';
    return `${displayHours}:${displayMinutes} ${ampm}`;
  }
}

/**
 * Formats a date to display full date in a readable format
 */
export function formatFullDate(date: Date): string {
  try {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.warn('Failed to format date with toLocaleDateString, using fallback:', error);
    // Fallback to a simple date format
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    const weekday = weekdays[date.getDay()];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${weekday}, ${month} ${day}, ${year}`;
  }
}

/**
 * Gets the time section for grouping items by recency
 */
export function getTimeSection(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const sixMonthsAgo = new Date(today);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  if (date >= today) {
    return 'Today';
  } else if (date >= weekAgo) {
    return 'This Week';
  } else if (date >= monthAgo) {
    return 'This Month';
  } else if (date >= threeMonthsAgo) {
    return 'Past 90 Days';
  } else if (date >= sixMonthsAgo) {
    return 'Past 180 Days';
  } else {
    return 'More than 180 Days';
  }
}

/**
 * Filters items by title using simple search
 */
export function filterItemsByTitle<T extends { title?: string }>(items: T[], query: string): T[] {
  if (!query.trim()) return items;

  const queryLower = query.toLowerCase();
  return items.filter((item) => {
    const itemTitle = item.title?.toLowerCase() || '';
    return itemTitle.includes(queryLower);
  });
}
